import { UtilService } from '../../../../../shared/services/util.service';

/**
 * Múltiplo (§6.2.3): produto vendido pela embalagem. A quantidade do item é SEMPRE
 * `unidades de múltiplo × fator`, e a conferência "é múltipla do fator" espelha a da API.Produto
 * (`ProdutoController.ValidarMultiploComposicaoKit`): fator e quantidade nas casas de quantidade,
 * resto com tolerância dos dois lados.
 */

/** `ProdutoController.toleranciaMultiplo` — o mesmo número. */
export const TOLERANCIA_MULTIPLO = 0.0001;

export interface RegraDoMultiplo {
    fatorMultiplo: number;
    /** Sigla da unidade do múltiplo (ex.: "CX"). */
    unidadeMultiplo: string;
}

/** A regra do produto, ou nulo quando ele não controla múltiplo (ou o cadastro está incompleto). */
export function regraDoMultiplo(produto: {
    isControlaMultiplo?: boolean | null;
    fatorMultiplo?: number | null;
    unidadeMultiplo?: string | null;
}): RegraDoMultiplo | null {
    const fator = Number(produto.fatorMultiplo ?? 0);

    if (produto.isControlaMultiplo !== true || fator <= 0) return null;

    return { fatorMultiplo: fator, unidadeMultiplo: produto.unidadeMultiplo ?? '' };
}

/** `unidades × fator`, nas casas de quantidade da empresa (a folha arredonda a quantidade nelas). */
export function quantidadeDoMultiplo(unidades: number, fatorMultiplo: number, casasQuantidade: number): number {
    return UtilService.arredondarAbnt((Number(unidades) || 0) * fatorMultiplo, casasQuantidade);
}

/** A equivalência em unidades de múltiplo, para o texto de apoio da lista ("6 CX"). */
export function unidadesDoMultiplo(quantidade: number, fatorMultiplo: number): number {
    return fatorMultiplo > 0 ? (Number(quantidade) || 0) / fatorMultiplo : 0;
}

/**
 * Espelho de `ValidarMultiploComposicaoKit`: o resto pode estar logo ACIMA de um múltiplo
 * (resto ~ 0) ou logo ABAIXO do próximo (resto ~ fator) — os dois são ruído de arredondamento.
 */
export function quantidadeEhMultipla(quantidade: number, fatorMultiplo: number, casasQuantidade: number): boolean {
    const fator = UtilService.arredondarAbnt(fatorMultiplo, casasQuantidade);
    const qtd = UtilService.arredondarAbnt(Number(quantidade) || 0, casasQuantidade);

    if (qtd <= 0 || fator <= 0) return false;

    const resto = qtd % fator;

    return resto <= TOLERANCIA_MULTIPLO || fator - resto <= TOLERANCIA_MULTIPLO;
}

/** Mensagem da §15 para quantidade que não é múltipla do fator. */
export function mensagemDoMultiplo(descricao: string, regra: RegraDoMultiplo, casasQuantidade: number): string {
    const fator = UtilService.arredondarAbnt(regra.fatorMultiplo, casasQuantidade)
        .toLocaleString('pt-BR', { maximumFractionDigits: casasQuantidade });

    return `${descricao} é vendido em ${regra.unidadeMultiplo} de ${fator}. Ajuste a quantidade.`;
}
